import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { Check } from "lucide-react";
import {
  fetchUserByEmail,
  fetchCardByUsername,
  createCardRequest,
  syncUser,
  UserCreate,
  Card,
  User,
} from "../utils/api";
import Button from "./ui/Button";

interface ShippingData {
  fullName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

const getPerCardPrice = (qty: number) => {
  if (qty >= 50) return 11.24;
  if (qty >= 25) return 11.69;
  if (qty >= 10) return 11.99;
  if (qty >= 5) return 12.74;
  if (qty >= 3) return 13.49;
  return 14.99;
};

const getDiscount = (qty: number) => {
  if (qty >= 50) return '25% off';
  if (qty >= 25) return '22% off';
  if (qty >= 10) return '20% off';
  if (qty >= 5) return '15% off';
  if (qty >= 3) return '10% off';
  return '';
};

const MAX_LOGO_SIZE = 2 * 1024 * 1024; // 2MB
const ACCEPTED_IMAGE_TYPES = "image/png,image/jpeg,image/webp,image/svg+xml";

const OrderCard: React.FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();

  // Profile / card data loaded from API
  const [dbUser, setDbUser] = useState<User | null>(null);
  const [card, setCard] = useState<Card | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Card details form
  const [nameOnCard, setNameOnCard] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [fieldIndustry, setFieldIndustry] = useState("");

  // Logo uploads
  const [wideLogo, setWideLogo] = useState<File | null>(null);
  const [wideLogoPreview, setWideLogoPreview] = useState<string | null>(null);
  const [squareLogo, setSquareLogo] = useState<File | null>(null);
  const [squareLogoPreview, setSquareLogoPreview] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);

  // Order options
  const [quantity, setQuantity] = useState(1);
  const [shipping, setShipping] = useState<ShippingData>({
    fullName: "",
    address: "",
    city: "",
    state: "",
    zip: "",
  });

  // Team order mode
  const [isTeamOrder, setIsTeamOrder] = useState(false);
  const [teamMembers, setTeamMembers] = useState<Array<{name: string; position: string; company: string}>>([
    { name: '', position: '', company: '' }
  ]);

  // UI state
  const [status, setStatus] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [loading, setLoading] = useState(false);

  // Load user profile & card from API
  const loadProfile = useCallback(async () => {
    if (!user?.primaryEmailAddress?.emailAddress) return;
    setLoadingProfile(true);
    setProfileError(null);

    try {
      const email = user.primaryEmailAddress.emailAddress;
      const fetchedUser = await fetchUserByEmail(email);
      setDbUser(fetchedUser);

      // Pre-fill shipping name
      setShipping((s) => ({
        ...s,
        fullName: s.fullName || [user.firstName, user.lastName].filter(Boolean).join(" "),
      }));

      // Pre-fill name on card from user profile
      setNameOnCard(
        [user.firstName, user.lastName].filter(Boolean).join(" ") || ""
      );

      // Pre-fill company if available
      if (fetchedUser.company) {
        setCompanyName(fetchedUser.company);
      }

      // Attempt to load their digital card
      try {
        const username = fetchedUser.username || email.split("@")[0];
        const fetchedCard = await fetchCardByUsername(username);
        setCard(fetchedCard);

        // Override with card data if available
        if (fetchedCard.name) setNameOnCard(fetchedCard.name);
        if (fetchedCard.company) setCompanyName(fetchedCard.company);
        if (fetchedCard.position) setFieldIndustry(fetchedCard.position);
      } catch {
        // No card yet - use Clerk info
      }
    } catch {
      setProfileError(
        "We couldn't find your profile. Please create your digital card on /my-card first."
      );
    } finally {
      setLoadingProfile(false);
    }
  }, [user]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    document.title = "Order Physical Card - WhoBrew";
  }, []);

  // Derived values
  // BL-008: the public card URL is the account handle (users.username), not the
  // per-card slug. Prefer the handle; fall back to the email-derived stub only
  // for display before a handle exists.
  const username =
    dbUser?.username ||
    (user?.primaryEmailAddress?.emailAddress?.split("@")[0] ?? "username");
  const publicUrl = `https://whobrew.us/card/${username}`;
  const displayEmail =
    card?.email || user?.primaryEmailAddress?.emailAddress || "";
  const displayPhone =
    card?.phoneNumber || user?.phoneNumbers?.[0]?.phoneNumber || "";

  // Logo handling
  const handleLogoUpload = (
    file: File | null,
    setFile: (f: File | null) => void,
    setPreview: (p: string | null) => void
  ) => {
    setLogoError(null);
    if (!file) {
      setFile(null);
      setPreview(null);
      return;
    }
    if (file.size > MAX_LOGO_SIZE) {
      setLogoError("Logo must be under 2MB.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      setLogoError("Please upload an image file.");
      return;
    }
    setFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleShippingChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof ShippingData
  ) => {
    setShipping((s) => ({ ...s, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!user) {
      setStatus({ message: "Please sign in to place an order.", type: "error" });
      return;
    }

    if (isTeamOrder) {
      const emptyMembers = teamMembers.filter((m) => !m.name.trim());
      if (emptyMembers.length > 0) {
        setStatus({ message: "Please enter a name for every team member.", type: "error" });
        return;
      }
    } else {
      if (!nameOnCard.trim()) {
        setStatus({ message: "Please enter a name for your card.", type: "error" });
        return;
      }
    }

    const requiredShipping: (keyof ShippingData)[] = ["fullName", "address", "city", "state", "zip"];
    const missing = requiredShipping.filter((f) => !shipping[f].trim());
    if (missing.length > 0) {
      setStatus({ message: "Please fill out all shipping fields.", type: "error" });
      return;
    }

    try {
      setLoading(true);
      setStatus(null);

      // Ensure user exists in backend
      let resolvedUser = dbUser;
      if (!resolvedUser) {
        const userData: UserCreate = {
          username,
          email: displayEmail,
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          phoneNumber: displayPhone,
          shippingAddress: `${shipping.address}, ${shipping.city}, ${shipping.state} ${shipping.zip}`,
        };
        resolvedUser = await syncUser(userData);
      }

      if (isTeamOrder) {
        const pricePerCard = getPerCardPrice(teamMembers.length);
        const totalPrice = teamMembers.length * pricePerCard;

        await createCardRequest({
          name: `Team Order by ${shipping.fullName}`,
          websiteUrl: publicUrl,
          position1: "",
          email: displayEmail,
          phoneNumber: displayPhone,
          comments: [
            `TEAM ORDER: ${teamMembers.length}x Black NFC Card(s) -- $${totalPrice.toFixed(2)} ($${pricePerCard.toFixed(2)}/card)`,
            wideLogo ? `Wide logo uploaded: ${wideLogo.name}` : "",
            squareLogo ? `Square logo uploaded: ${squareLogo.name}` : "",
            `Ship to: ${shipping.fullName}`,
            `${shipping.address}, ${shipping.city}, ${shipping.state} ${shipping.zip}`,
            `\nTEAM MEMBERS JSON:\n${JSON.stringify(teamMembers, null, 2)}`,
          ]
            .filter(Boolean)
            .join("\n"),
          status: "pending",
        });
      } else {
        const pricePerCard = getPerCardPrice(quantity);
        const totalPrice = quantity * pricePerCard;

        await createCardRequest({
          name: nameOnCard,
          websiteUrl: publicUrl,
          position1: fieldIndustry || "",
          email: displayEmail,
          phoneNumber: displayPhone,
          comments: [
            `ORDER: ${quantity}x Black NFC Card(s) -- $${totalPrice.toFixed(2)} ($${pricePerCard.toFixed(2)}/card)`,
            companyName ? `Company: ${companyName}` : "",
            fieldIndustry ? `Field/Industry: ${fieldIndustry}` : "",
            wideLogo ? `Wide logo uploaded: ${wideLogo.name}` : "",
            squareLogo ? `Square logo uploaded: ${squareLogo.name}` : "",
            `Ship to: ${shipping.fullName}`,
            `${shipping.address}, ${shipping.city}, ${shipping.state} ${shipping.zip}`,
          ]
            .filter(Boolean)
            .join("\n"),
          status: "pending",
        });
      }

      navigate("/order-confirmation", {
        state: { cardName: isTeamOrder ? `Team Order (${teamMembers.length} cards)` : `${nameOnCard} (${quantity} card${quantity > 1 ? 's' : ''})`, cardEmail: displayEmail },
      });
    } catch (err: any) {
      setStatus({
        message: err.message || "Failed to place order. Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // --- Render ---

  if (loadingProfile) {
    return (
      <div className="min-h-screen bg-white text-coffee-900 dark:text-cream-50">
        <div className="border-b border-coffee-100 dark:border-coffee-800 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <div className="animate-pulse space-y-2">
              <div className="h-3 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded w-24" />
              <div className="h-8 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded-2xl w-72" />
              <div className="h-4 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded w-48" />
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-pulse">
            <div className="space-y-4">
              <div className="h-64 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded-2xl" />
              <div className="h-48 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded-2xl" />
            </div>
            <div className="h-96 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (profileError) {
    return (
      <div className="min-h-screen bg-white text-coffee-900 dark:text-cream-50">
        <div className="border-b border-coffee-100 dark:border-coffee-800 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <div className="eyebrow mb-1.5">Order a card</div>
            <h1 className="text-display text-3xl sm:text-4xl text-coffee-900 dark:text-cream-50">Get a physical NFC card.</h1>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="w-12 h-12 rounded-xl bg-red-50 border border-red-200 flex items-center justify-center mx-auto mb-4">
            <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          </div>
          <h3 className="font-semibold text-coffee-900 dark:text-cream-50 mb-1">Profile not found</h3>
          <p className="text-sm text-coffee-600 dark:text-cream-200 max-w-xs mx-auto mb-4">{profileError}</p>
          <Button onClick={() => navigate("/my-card")} variant="primary">Create your digital card</Button>
        </div>
      </div>
    );
  }

  const orderQty = isTeamOrder ? teamMembers.length : quantity;
  const pricePerCard = getPerCardPrice(orderQty);
  const totalPrice = orderQty * pricePerCard;
  const discount = getDiscount(orderQty);

  return (
    <div className="min-h-screen bg-white text-coffee-900 dark:text-cream-50">
      {/* Page header */}
      <div className="border-b border-coffee-100 dark:border-coffee-800 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="eyebrow mb-1.5">Order a card</div>
              <h1 className="text-display text-3xl sm:text-4xl text-coffee-900 dark:text-cream-50">Get a physical NFC card.</h1>
              <p className="mt-1.5 text-coffee-600 dark:text-cream-200">
                <span className="font-mono text-coffee-900 dark:text-cream-50">$14.99</span> — ships in 5–7 days. Free shipping to the Continental US.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Order type toggle bar */}
      <div className="border-b border-coffee-100 dark:border-coffee-800 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="inline-flex gap-1 bg-white border border-coffee-100 dark:border-coffee-800 rounded-xl p-1">
            <button
              onClick={() => setIsTeamOrder(false)}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                !isTeamOrder ? 'bg-coffee-900 text-white' : 'text-coffee-600 hover:text-coffee-900'
              }`}
            >
              For myself
            </button>
            <button
              onClick={() => setIsTeamOrder(true)}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                isTeamOrder ? 'bg-coffee-900 text-white' : 'text-coffee-600 hover:text-coffee-900'
              }`}
            >
              For my team
            </button>
          </div>
        </div>
      </div>

      {/* Main content + sticky sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Status message */}
        {status && (
          <div className={`mb-6 p-4 rounded-xl border text-sm ${
            status.type === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-red-50 border-red-200 text-red-700"
          }`}>
            {status.message}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left: forms (col-span-7) */}
          <div className="lg:col-span-7 space-y-6">

            {/* Team Members (team order mode) */}
            {isTeamOrder && (
              <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl p-6 shadow-coffee">
                <div className="eyebrow mb-3">Team members</div>
                <p className="text-sm text-coffee-600 dark:text-cream-200 mb-4">Each card can have different information. All cards link to each person's WhoBrew profile.</p>
                <div className="space-y-3">
                  {teamMembers.map((member, index) => (
                    <div key={index} className="flex gap-3 items-start">
                      <span className="font-mono text-xs text-coffee-400 dark:text-cream-400 mt-2.5 w-6 text-right flex-shrink-0">{String(index + 1).padStart(2, '0')}</span>
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <input
                          value={member.name}
                          onChange={(e) => {
                            const updated = [...teamMembers];
                            updated[index].name = e.target.value;
                            setTeamMembers(updated);
                          }}
                          className="px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded-lg text-sm bg-white text-coffee-900 dark:text-cream-50 focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500"
                          placeholder="Full name"
                        />
                        <input
                          value={member.position}
                          onChange={(e) => {
                            const updated = [...teamMembers];
                            updated[index].position = e.target.value;
                            setTeamMembers(updated);
                          }}
                          className="px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded-lg text-sm bg-white text-coffee-900 dark:text-cream-50 focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500"
                          placeholder="Title / Position"
                        />
                        <input
                          value={member.company}
                          onChange={(e) => {
                            const updated = [...teamMembers];
                            updated[index].company = e.target.value;
                            setTeamMembers(updated);
                          }}
                          className="px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded-lg text-sm bg-white text-coffee-900 dark:text-cream-50 focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500"
                          placeholder="Company"
                        />
                      </div>
                      {teamMembers.length > 1 && (
                        <button
                          onClick={() => setTeamMembers(teamMembers.filter((_, i) => i !== index))}
                          className="text-red-400 hover:text-red-600 text-xs mt-2.5 transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setTeamMembers([...teamMembers, { name: '', position: '', company: '' }])}
                  className="mt-4 text-sm text-coffee-600 dark:text-cream-200 hover:text-coffee-800 font-medium transition-colors"
                >
                  + Add another member
                </button>
              </div>
            )}

            {/* Card Details Form (single card mode) */}
            {!isTeamOrder && (
              <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl p-6 shadow-coffee">
                <div className="eyebrow mb-3">Card details</div>
                <div className="space-y-5">
                  <div>
                    <label className="block eyebrow mb-2 text-coffee-500 dark:text-cream-300">
                      Name on card <span className="normal-case text-red-500 text-xs ml-1">required</span>
                    </label>
                    <input
                      type="text"
                      value={nameOnCard}
                      onChange={(e) => setNameOnCard(e.target.value)}
                      className="w-full px-4 py-3 border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors bg-white text-coffee-900 dark:text-cream-50"
                      placeholder="Your Name"
                    />
                  </div>
                  <div>
                    <label className="block eyebrow mb-2 text-coffee-500 dark:text-cream-300">
                      Company name <span className="normal-case text-coffee-400 dark:text-cream-400 text-xs ml-1">optional</span>
                    </label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="w-full px-4 py-3 border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors bg-white text-coffee-900 dark:text-cream-50"
                      placeholder="Your Company"
                    />
                  </div>
                  <div>
                    <label className="block eyebrow mb-2 text-coffee-500 dark:text-cream-300">
                      Field / industry <span className="normal-case text-coffee-400 dark:text-cream-400 text-xs ml-1">optional</span>
                    </label>
                    <input
                      type="text"
                      value={fieldIndustry}
                      onChange={(e) => setFieldIndustry(e.target.value)}
                      className="w-full px-4 py-3 border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors bg-white text-coffee-900 dark:text-cream-50"
                      placeholder='e.g. "Software Engineering", "Real Estate", "Recruiting"'
                    />
                  </div>

                  {/* Logo Uploads */}
                  {logoError && (
                    <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                      {logoError}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block eyebrow mb-2 text-coffee-500 dark:text-cream-300">
                        Wide / horizontal logo <span className="normal-case text-coffee-400 dark:text-cream-400 text-xs ml-1">for top of card</span>
                      </label>
                      <input
                        type="file"
                        accept={ACCEPTED_IMAGE_TYPES}
                        onChange={(e) =>
                          handleLogoUpload(e.target.files?.[0] || null, setWideLogo, setWideLogoPreview)
                        }
                        className="w-full text-sm text-coffee-700 dark:text-cream-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-coffee-100 file:text-coffee-700 hover:file:bg-coffee-200 transition-colors"
                      />
                      <p className="text-xs text-coffee-400 dark:text-cream-400 mt-1">Max 2MB, image files only</p>
                      {wideLogoPreview && (
                        <div className="mt-3 p-2 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded-lg inline-block">
                          <img src={wideLogoPreview} alt="Wide logo preview" className="max-h-16 max-w-full object-contain" />
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block eyebrow mb-2 text-coffee-500 dark:text-cream-300">
                        Square logo <span className="normal-case text-coffee-400 dark:text-cream-400 text-xs ml-1">optional</span>
                      </label>
                      <input
                        type="file"
                        accept={ACCEPTED_IMAGE_TYPES}
                        onChange={(e) =>
                          handleLogoUpload(e.target.files?.[0] || null, setSquareLogo, setSquareLogoPreview)
                        }
                        className="w-full text-sm text-coffee-700 dark:text-cream-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-coffee-100 file:text-coffee-700 hover:file:bg-coffee-200 transition-colors"
                      />
                      <p className="text-xs text-coffee-400 dark:text-cream-400 mt-1">Max 2MB, image files only</p>
                      {squareLogoPreview && (
                        <div className="mt-3 p-2 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded-lg inline-block">
                          <img src={squareLogoPreview} alt="Square logo preview" className="max-h-16 max-w-16 object-contain" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quantity Selector */}
                  <div>
                    <label className="block eyebrow mb-2 text-coffee-500 dark:text-cream-300">Quantity</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min={1}
                        max={100}
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-24 px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded-lg bg-white text-coffee-900 dark:text-cream-50 focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500"
                      />
                      {discount && (
                        <span className="text-sm text-emerald-700 font-semibold bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">{discount}</span>
                      )}
                    </div>
                    <p className="text-sm text-coffee-600 dark:text-cream-200 mt-1.5">
                      <span className="font-mono text-coffee-900 dark:text-cream-50">${pricePerCard.toFixed(2)}/card</span>
                      {' — '}Total: <span className="font-mono text-coffee-900 dark:text-cream-50">${totalPrice.toFixed(2)}</span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Shipping Information */}
            <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl p-6 shadow-coffee">
              <div className="eyebrow mb-1.5">Shipping</div>
              <p className="text-sm text-coffee-600 dark:text-cream-200 mb-4">Free shipping to the Continental US.</p>
              <div className="space-y-4">
                <div>
                  <label className="block eyebrow mb-2 text-coffee-500 dark:text-cream-300">
                    Full name <span className="normal-case text-red-500 text-xs ml-1">required</span>
                  </label>
                  <input
                    type="text"
                    value={shipping.fullName}
                    onChange={(e) => handleShippingChange(e, "fullName")}
                    className="w-full px-4 py-3 border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors bg-white text-coffee-900 dark:text-cream-50"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block eyebrow mb-2 text-coffee-500 dark:text-cream-300">
                    Street address <span className="normal-case text-red-500 text-xs ml-1">required</span>
                  </label>
                  <input
                    type="text"
                    value={shipping.address}
                    onChange={(e) => handleShippingChange(e, "address")}
                    className="w-full px-4 py-3 border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors bg-white text-coffee-900 dark:text-cream-50"
                    placeholder="123 Main St"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block eyebrow mb-2 text-coffee-500 dark:text-cream-300">City</label>
                    <input
                      type="text"
                      value={shipping.city}
                      onChange={(e) => handleShippingChange(e, "city")}
                      className="w-full px-4 py-3 border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors bg-white text-coffee-900 dark:text-cream-50"
                      placeholder="Austin"
                    />
                  </div>
                  <div>
                    <label className="block eyebrow mb-2 text-coffee-500 dark:text-cream-300">State</label>
                    <input
                      type="text"
                      value={shipping.state}
                      onChange={(e) => handleShippingChange(e, "state")}
                      className="w-full px-4 py-3 border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors bg-white text-coffee-900 dark:text-cream-50"
                      placeholder="TX"
                      maxLength={2}
                    />
                  </div>
                </div>
                <div className="w-1/2">
                  <label className="block eyebrow mb-2 text-coffee-500 dark:text-cream-300">ZIP code</label>
                  <input
                    type="text"
                    value={shipping.zip}
                    onChange={(e) => handleShippingChange(e, "zip")}
                    className="w-full px-4 py-3 border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors bg-white text-coffee-900 dark:text-cream-50"
                    placeholder="78701"
                    maxLength={10}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right: preview + summary sidebar (col-span-5) */}
          <div className="lg:col-span-5 space-y-5 lg:sticky lg:top-4">

            {/* Live card preview */}
            <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl p-5 shadow-coffee">
              <div className="eyebrow mb-4">Card preview</div>
              <div className="space-y-4">
                {/* Front */}
                <div>
                  <p className="text-[10px] tracking-eyebrow font-semibold text-coffee-400 dark:text-cream-400 mb-2">FRONT</p>
                  <div
                    className="w-full aspect-[1.586/1] rounded-xl p-5 flex flex-col justify-between relative overflow-hidden"
                    style={{
                      background: "linear-gradient(145deg, #232323 0%, #111111 50%, #000000 100%)",
                      boxShadow: "0 12px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.08)",
                    }}
                  >
                    <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(125deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 30%, transparent 50%)" }} />
                    <div className="flex items-start relative z-10">
                      {wideLogoPreview ? (
                        <img src={wideLogoPreview} alt="Logo" className="max-h-6 max-w-[120px] object-contain brightness-0 invert" />
                      ) : squareLogoPreview ? (
                        <img src={squareLogoPreview} alt="Logo" className="max-h-6 max-w-6 object-contain brightness-0 invert" />
                      ) : companyName ? (
                        <span className="text-white text-xs font-medium tracking-wide opacity-70">{companyName}</span>
                      ) : null}
                    </div>
                    <div className="relative z-10">
                      {fieldIndustry && <p className="text-white text-[10px] tracking-widest opacity-50 mb-1 uppercase">{fieldIndustry}</p>}
                      <p className="text-white text-base font-bold tracking-wide">{isTeamOrder ? (teamMembers[0]?.name || 'Team Member') : (nameOnCard || 'Your Name')}</p>
                    </div>
                  </div>
                </div>
                {/* Back */}
                <div>
                  <p className="text-[10px] tracking-eyebrow font-semibold text-coffee-400 dark:text-cream-400 mb-2">BACK</p>
                  <div
                    className="w-full aspect-[1.586/1] rounded-xl flex flex-col items-center justify-center relative overflow-hidden"
                    style={{
                      background: "linear-gradient(145deg, #232323 0%, #111111 50%, #000000 100%)",
                      boxShadow: "0 12px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.08)",
                    }}
                  >
                    <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(125deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 30%, transparent 50%)" }} />
                    <div className="w-16 h-16 rounded-lg flex items-center justify-center mb-2 relative z-10" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)" }}>
                      <svg width="36" height="36" viewBox="0 0 48 48" fill="none">
                        <rect x="4" y="4" width="16" height="16" rx="2" stroke="white" strokeWidth="2" fill="none" />
                        <rect x="8" y="8" width="8" height="8" rx="1" fill="white" />
                        <rect x="28" y="4" width="16" height="16" rx="2" stroke="white" strokeWidth="2" fill="none" />
                        <rect x="32" y="8" width="8" height="8" rx="1" fill="white" />
                        <rect x="4" y="28" width="16" height="16" rx="2" stroke="white" strokeWidth="2" fill="none" />
                        <rect x="8" y="32" width="8" height="8" rx="1" fill="white" />
                        <rect x="28" y="28" width="4" height="4" fill="white" />
                        <rect x="36" y="28" width="4" height="4" fill="white" />
                        <rect x="28" y="36" width="4" height="4" fill="white" />
                        <rect x="36" y="36" width="8" height="8" rx="1" fill="white" />
                      </svg>
                    </div>
                    <p className="text-white text-sm font-bold tracking-wide relative z-10 mb-0.5">Tap In!</p>
                    <p className="text-white text-[10px] opacity-40 relative z-10 font-mono">whobrew.us/card/{username}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* What's included */}
            <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl p-5 shadow-coffee">
              <div className="eyebrow mb-3">What's included</div>
              <ul className="space-y-2">
                {[
                  'NFC chip — tap to share on any phone',
                  'Printed QR code backup',
                  'Link to your WhoBrew card page',
                  'Free updates — edit links any time',
                  'Free shipping, Continental US',
                ].map(item => (
                  <li key={item} className="flex items-start gap-2 text-sm text-coffee-700 dark:text-cream-200">
                    <Check className="w-4 h-4 text-coffee-600 dark:text-cream-200 flex-shrink-0 mt-0.5" strokeWidth={3} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Order summary + CTA */}
            <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl p-5 shadow-coffee">
              <div className="eyebrow mb-3">Order summary</div>
              <div className="space-y-2 text-sm text-coffee-700 dark:text-cream-200 mb-4">
                <div className="flex justify-between">
                  <span>Quantity</span>
                  <span className="font-mono text-coffee-900 dark:text-cream-50">{orderQty} {orderQty === 1 ? 'card' : 'cards'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Price per card</span>
                  <span className="font-mono text-coffee-900 dark:text-cream-50">
                    ${pricePerCard.toFixed(2)}
                    {discount && <span className="ml-1.5 text-emerald-700 text-xs font-semibold">({discount})</span>}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span className="text-emerald-700 font-semibold">Free</span>
                </div>
                <div className="pt-2 border-t border-coffee-100 dark:border-coffee-800 flex justify-between font-semibold text-coffee-900 dark:text-cream-50">
                  <span>Total</span>
                  <span className="font-mono text-lg">${totalPrice.toFixed(2)}</span>
                </div>
              </div>

              <Button
                variant="primary"
                fullWidth
                size="lg"
                loading={loading}
                onClick={handleSubmit}
              >
                {loading ? 'Placing order...' : 'Place order'}
              </Button>
              <p className="text-xs text-coffee-500 dark:text-cream-300 text-center mt-2">
                Ships in 5–7 business days.{' '}
                <a href="/contact" className="text-coffee-700 dark:text-cream-200 underline hover:text-coffee-900">Questions?</a>
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderCard;
